/**
 * Mailgun Data Transformation Utilities
 * 
 * This module provides utilities for transforming Mailgun API responses into
 * application data formats. It centralizes the mapping logic for event types,
 * status transformations, and data extraction from Mailgun events.
 */

import type { MailgunEvent, MessageTrackingData, EnhancedMessageTrackingData, CampaignStats } from '../services/outreach/types';

/**
 * Mailgun event types as defined by their API
 */
export type MailgunEventType = 
  | 'accepted'
  | 'delivered' 
  | 'failed'
  | 'opened'
  | 'clicked'
  | 'unsubscribed'
  | 'complained'
  | 'rejected'
  | 'stored';

/**
 * Application internal status types for message tracking
 */
export type ApplicationStatusType = 
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'failed';

/**
 * Comprehensive mapping dictionary for Mailgun event types to application status types
 * 
 * The mapping follows this progression:
 * pending -> sent (accepted) -> delivered -> opened -> clicked
 * 
 * Failed events override any other status as they represent terminal error states.
 */
export const MAILGUN_EVENT_STATUS_MAP: Record<MailgunEventType, ApplicationStatusType> = {
  // Initial acceptance and processing
  'accepted': 'sent',
  'stored': 'sent',
  
  // Successful delivery
  'delivered': 'delivered',
  
  // Engagement events (higher priority than delivery)
  'opened': 'opened',
  'clicked': 'clicked',
  
  // Error states (terminal)
  'failed': 'failed',
  'rejected': 'failed',
  
  // Negative engagement (treated as delivered for status purposes)
  'unsubscribed': 'delivered',
  'complained': 'delivered'
};

/**
 * Priority order for status determination when multiple events exist
 * Higher numbers indicate higher priority
 */
export const STATUS_PRIORITY: Record<ApplicationStatusType, number> = {
  'pending': 0,
  'sent': 1,
  'delivered': 2,
  'opened': 3,
  'clicked': 4,
  'failed': 5 // Highest priority as it's a terminal error state
};

/**
 * Maps a Mailgun event type to the corresponding application status type
 * 
 * @param eventType - The Mailgun event type to map
 * @returns The corresponding application status type
 * @throws Error if the event type is unknown
 * 
 * @example
 * ```typescript
 * const status = mapEventTypeToStatus('delivered'); // Returns 'delivered'
 * const status = mapEventTypeToStatus('opened'); // Returns 'opened'
 * ```
 */
export const mapEventTypeToStatus = (eventType: string): ApplicationStatusType => {
  // Handle case-insensitive input
  const normalizedEventType = eventType.toLowerCase() as MailgunEventType;
  
  // Check if the event type exists in our mapping
  if (normalizedEventType in MAILGUN_EVENT_STATUS_MAP) {
    return MAILGUN_EVENT_STATUS_MAP[normalizedEventType];
  }
  
  // Handle unknown event types with a fallback
  console.warn(`Unknown Mailgun event type: ${eventType}. Falling back to 'pending' status.`);
  return 'pending';
};

/**
 * Determines the highest priority status from an array of Mailgun events
 * 
 * @param events - Array of Mailgun events to analyze
 * @returns The highest priority application status type
 * 
 * @example
 * ```typescript
 * const events = [
 *   { event: 'accepted', ... },
 *   { event: 'delivered', ... },
 *   { event: 'opened', ... }
 * ];
 * const status = getHighestPriorityStatus(events); // Returns 'opened'
 * ```
 */
export const getHighestPriorityStatus = (events: MailgunEvent[]): ApplicationStatusType => {
  if (events.length === 0) {
    return 'pending';
  }
  
  let highestStatus: ApplicationStatusType = 'pending';
  let highestPriority = STATUS_PRIORITY['pending'];
  
  for (const event of events) {
    const status = mapEventTypeToStatus(event.event);
    const priority = STATUS_PRIORITY[status];
    
    if (priority > highestPriority) {
      highestStatus = status;
      highestPriority = priority;
    }
  }
  
  return highestStatus;
};

/**
 * Checks if an event type represents a failure state
 * 
 * @param eventType - The Mailgun event type to check
 * @returns True if the event represents a failure
 */
export const isFailureEvent = (eventType: string): boolean => {
  const normalizedEventType = eventType.toLowerCase();
  return normalizedEventType === 'failed' || normalizedEventType === 'rejected';
};

/**
 * Checks if an event type represents an engagement action
 * 
 * @param eventType - The Mailgun event type to check
 * @returns True if the event represents user engagement
 */
export const isEngagementEvent = (eventType: string): boolean => {
  const normalizedEventType = eventType.toLowerCase();
  return normalizedEventType === 'opened' || normalizedEventType === 'clicked';
};

/**
 * Gets all event types that match a specific application status
 * 
 * @param status - The application status to find event types for
 * @returns Array of Mailgun event types that map to the given status
 */
export const getEventTypesForStatus = (status: ApplicationStatusType): MailgunEventType[] => {
  return Object.entries(MAILGUN_EVENT_STATUS_MAP)
    .filter(([_, mappedStatus]) => mappedStatus === status)
    .map(([eventType, _]) => eventType as MailgunEventType);
};

/**
 * Validates that an event type is a known Mailgun event type
 * 
 * @param eventType - The event type to validate
 * @returns True if the event type is known
 */
export const isValidMailgunEventType = (eventType: string): eventType is MailgunEventType => {
  const normalizedEventType = eventType.toLowerCase();
  return normalizedEventType in MAILGUN_EVENT_STATUS_MAP;
};

// ============================================================================
// TIMESTAMP STANDARDIZATION UTILITIES
// ============================================================================

/**
 * Timestamp format options for different use cases
 */
export interface TimestampFormatOptions {
  /** Include timezone information in the output */
  includeTimezone?: boolean;
  /** Use local timezone instead of UTC */
  useLocalTimezone?: boolean;
  /** Format for UI display (e.g., 'short', 'medium', 'long', 'full') */
  displayFormat?: 'short' | 'medium' | 'long' | 'full' | 'relative';
  /** Custom date format string */
  customFormat?: string;
}

/**
 * Converts a Mailgun timestamp (Unix timestamp in seconds) to ISO string format
 * 
 * @param timestamp - Unix timestamp in seconds from Mailgun API
 * @returns ISO string representation of the timestamp
 * 
 * @example
 * ```typescript
 * const isoString = formatMailgunTimestamp(1640995200); // "2022-01-01T00:00:00.000Z"
 * ```
 */
export const formatMailgunTimestamp = (timestamp: number): string => {
  if (!timestamp || timestamp <= 0) {
    throw new Error('Invalid timestamp: must be a positive number');
  }
  
  // Mailgun timestamps are in seconds, JavaScript Date expects milliseconds
  const date = new Date(timestamp * 1000);
  
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid timestamp: ${timestamp} could not be converted to a valid date`);
  }
  
  return date.toISOString();
};

/**
 * Converts various timestamp formats to a standardized ISO string
 * Handles Unix timestamps (seconds/milliseconds), ISO strings, and Date objects
 * 
 * @param timestamp - The timestamp to standardize
 * @returns Standardized ISO string representation
 * 
 * @example
 * ```typescript
 * const iso1 = standardizeTimestamp(1640995200); // Unix seconds
 * const iso2 = standardizeTimestamp(1640995200000); // Unix milliseconds  
 * const iso3 = standardizeTimestamp("2022-01-01T00:00:00.000Z"); // ISO string
 * const iso4 = standardizeTimestamp(new Date()); // Date object
 * ```
 */
export const standardizeTimestamp = (timestamp: number | string | Date): string => {
  if (timestamp === null || timestamp === undefined || timestamp === '') {
    throw new Error('Invalid timestamp: timestamp is required');
  }
  
  // Handle Date objects
  if (timestamp instanceof Date) {
    if (isNaN(timestamp.getTime())) {
      throw new Error('Invalid timestamp: Date object is invalid');
    }
    return timestamp.toISOString();
  }
  
  // Handle string timestamps (ISO format)
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid timestamp: "${timestamp}" could not be parsed as a date`);
    }
    return date.toISOString();
  }
  
  // Handle numeric timestamps
  if (typeof timestamp === 'number') {
    if (timestamp === 0 || timestamp < 0) {
      throw new Error('Invalid timestamp: must be a positive number');
    }
    
    // Determine if timestamp is in seconds or milliseconds
    // If the number is greater than 1e12 (year 2001 in milliseconds), treat as milliseconds
    // Otherwise treat as seconds
    const isMilliseconds = timestamp > 1e12;
    
    const date = new Date(isMilliseconds ? timestamp : timestamp * 1000);
    
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid timestamp: ${timestamp} could not be converted to a valid date`);
    }
    
    return date.toISOString();
  }
  
  throw new Error(`Invalid timestamp: unsupported type ${typeof timestamp}`);
};

/**
 * Formats a timestamp for display in the UI with various format options
 * 
 * @param timestamp - The timestamp to format (Unix seconds, ISO string, or Date)
 * @param options - Formatting options
 * @returns Formatted timestamp string for UI display
 * 
 * @example
 * ```typescript
 * const short = formatTimestampForDisplay(1640995200, { displayFormat: 'short' });
 * // "1/1/2022"
 * 
 * const medium = formatTimestampForDisplay(1640995200, { displayFormat: 'medium' });
 * // "Jan 1, 2022, 12:00:00 AM"
 * 
 * const relative = formatTimestampForDisplay(Date.now() - 3600000, { displayFormat: 'relative' });
 * // "1 hour ago"
 * ```
 */
export const formatTimestampForDisplay = (
  timestamp: number | string | Date,
  options: TimestampFormatOptions = {}
): string => {
  const {
    displayFormat = 'medium',
    useLocalTimezone = true,
    includeTimezone = false
  } = options;
  
  // First standardize the timestamp
  const isoString = standardizeTimestamp(timestamp);
  const date = new Date(isoString);
  
  // Handle relative time format
  if (displayFormat === 'relative') {
    return formatRelativeTime(date);
  }
  
  // Configure locale options based on display format
  const localeOptions: Intl.DateTimeFormatOptions = {
    timeZone: useLocalTimezone ? undefined : 'UTC'
  };
  
  switch (displayFormat) {
    case 'short':
      localeOptions.dateStyle = 'short';
      break;
    case 'medium':
      localeOptions.dateStyle = 'medium';
      localeOptions.timeStyle = 'short';
      break;
    case 'long':
      localeOptions.dateStyle = 'long';
      localeOptions.timeStyle = 'medium';
      break;
    case 'full':
      localeOptions.dateStyle = 'full';
      localeOptions.timeStyle = 'long';
      break;
  }
  
  if (includeTimezone) {
    localeOptions.timeZoneName = 'short';
  }
  
  return date.toLocaleString(undefined, localeOptions);
};

/**
 * Formats a timestamp as relative time (e.g., "2 hours ago", "in 3 days")
 * 
 * @param date - The date to format relatively
 * @returns Relative time string
 */
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(Math.abs(diffMs) / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  // Future dates
  if (diffMs < 0) {
    if (diffSeconds < 60) return 'in a few seconds';
    if (diffMinutes < 60) return `in ${diffMinutes} minute${diffMinutes === 1 ? '' : 's'}`;
    if (diffHours < 24) return `in ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
    if (diffDays < 30) return `in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
    return date.toLocaleDateString();
  }
  
  // Past dates
  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  
  // For dates older than 30 days, show the actual date
  return date.toLocaleDateString();
};

/**
 * Validates that a timestamp string is in valid ISO format
 * 
 * @param timestamp - The timestamp string to validate
 * @returns True if the timestamp is valid ISO format
 * 
 * @example
 * ```typescript
 * const isValid1 = isValidISOTimestamp("2022-01-01T00:00:00.000Z"); // true
 * const isValid2 = isValidISOTimestamp("invalid-date"); // false
 * ```
 */
export const isValidISOTimestamp = (timestamp: string): boolean => {
  if (typeof timestamp !== 'string') {
    return false;
  }
  
  // Check basic ISO format pattern (allow optional milliseconds)
  const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;
  if (!isoPattern.test(timestamp)) {
    return false;
  }
  
  // Validate that it can be parsed as a valid date
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    return false;
  }
  
  // Check that the parsed date produces the same ISO string
  // This catches invalid dates like "2022-13-01" that might parse but are wrong
  return date.toISOString() === timestamp || 
         (timestamp.endsWith('Z') && !timestamp.includes('.') && 
          date.toISOString().replace(/\.\d{3}Z$/, 'Z') === timestamp);
};

/**
 * Gets the timezone offset for a given timestamp
 * 
 * @param timestamp - The timestamp to get timezone info for
 * @returns Timezone offset information
 */
export const getTimezoneInfo = (timestamp: number | string | Date) => {
  const date = new Date(standardizeTimestamp(timestamp));
  const offsetMinutes = date.getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  const offsetMins = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes <= 0 ? '+' : '-';
  
  return {
    offsetMinutes,
    offsetString: `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')}`,
    timezoneName: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

/**
 * Converts a timestamp to a specific timezone
 * 
 * @param timestamp - The timestamp to convert
 * @param targetTimezone - Target timezone (e.g., 'America/New_York', 'Europe/London')
 * @returns ISO string in the target timezone
 */
export const convertToTimezone = (
  timestamp: number | string | Date,
  targetTimezone: string
): string => {
  const date = new Date(standardizeTimestamp(timestamp));
  
  try {
    // Create a formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: targetTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
    
    // Format the date in the target timezone
    const formatted = formatter.format(date);
    
    // Convert the formatted string to ISO format
    // The 'sv-SE' locale gives us YYYY-MM-DD HH:mm:ss format
    const isoString = formatted.replace(' ', 'T') + '.000Z';
    
    return isoString;
  } catch (error) {
    throw new Error(`Invalid timezone: ${targetTimezone}`);
  }
};

// ============================================================================
// MESSAGE EVENT AGGREGATION UTILITIES
// ============================================================================

/**
 * Interface for tracking event history within a message
 */
export interface EventHistoryEntry {
  /** The Mailgun event type */
  eventType: MailgunEventType;
  /** When the event occurred */
  timestamp: string;
  /** Additional event-specific data */
  eventData?: {
    userAgent?: string;
    clientInfo?: {
      name: string;
      os: string;
      device: string;
    };
    geolocation?: {
      country: string;
      region: string;
      city: string;
    };
    url?: string;
    ip?: string;
    deliveryStatus?: {
      message?: string;
      code?: number;
    };
  };
}

/**
 * Interface for aggregated message tracking data
 */
export interface AggregatedMessageData extends EnhancedMessageTrackingData {
  /** Complete chronological history of all events for this message */
  eventHistory: EventHistoryEntry[];
  /** Number of times the message was opened (for multiple opens) */
  openCount: number;
  /** Number of times links were clicked (for multiple clicks) */
  clickCount: number;
  /** Last time the message was updated with new event data */
  lastUpdated: string;
}

/**
 * Aggregates multiple Mailgun events for a single message into a coherent tracking record
 * 
 * @param events - Array of Mailgun events for the same message (should have same message ID)
 * @param existingData - Optional existing tracking data to merge with
 * @returns Aggregated message tracking data with complete event history
 * 
 * @example
 * ```typescript
 * const events = [
 *   { event: 'accepted', timestamp: 1640995200, ... },
 *   { event: 'delivered', timestamp: 1640995260, ... },
 *   { event: 'opened', timestamp: 1640995320, ... }
 * ];
 * const aggregated = aggregateMessageEvents(events);
 * // Returns complete tracking data with status 'opened' and full event history
 * ```
 */
export const aggregateMessageEvents = (
  events: MailgunEvent[],
  existingData?: AggregatedMessageData
): AggregatedMessageData => {
  if (events.length === 0) {
    throw new Error('Cannot aggregate empty events array');
  }

  // Validate that all events are for the same message
  const messageId = events[0].message?.headers?.['message-id'];
  if (!messageId) {
    throw new Error('Events must contain valid message ID');
  }

  const invalidEvents = events.filter(event => 
    event.message?.headers?.['message-id'] !== messageId
  );
  if (invalidEvents.length > 0) {
    throw new Error(`All events must be for the same message. Found ${invalidEvents.length} events with different message IDs`);
  }

  // Sort events chronologically
  const sortedEvents = [...events].sort((a, b) => a.timestamp - b.timestamp);
  
  // Get the highest priority status from all events
  const finalStatus = getHighestPriorityStatus(sortedEvents);
  
  // Build event history
  const eventHistory: EventHistoryEntry[] = sortedEvents.map(event => ({
    eventType: event.event as MailgunEventType,
    timestamp: formatMailgunTimestamp(event.timestamp),
    eventData: {
      userAgent: event['client-info']?.['user-agent'],
      clientInfo: event['client-info'] ? {
        name: event['client-info']['client-name'] || 'Unknown',
        os: event['client-info']['client-os'] || 'Unknown',
        device: event['client-info']['device-type'] || 'Unknown'
      } : undefined,
      geolocation: event.geolocation ? {
        country: event.geolocation.country || 'Unknown',
        region: event.geolocation.region || 'Unknown',
        city: event.geolocation.city || 'Unknown'
      } : undefined,
      url: event.url,
      ip: event.ip,
      deliveryStatus: event['delivery-status']
    }
  }));

  // Merge with existing event history if provided
  const allEventHistory = existingData 
    ? mergeEventHistories(existingData.eventHistory, eventHistory)
    : eventHistory;

  // Calculate engagement metrics
  const openCount = allEventHistory.filter(e => e.eventType === 'opened').length;
  const clickCount = allEventHistory.filter(e => e.eventType === 'clicked').length;

  // Find specific event timestamps
  const deliveredEvent = allEventHistory.find(e => e.eventType === 'delivered');
  const openedEvent = allEventHistory.find(e => e.eventType === 'opened');
  const clickedEvent = allEventHistory.find(e => e.eventType === 'clicked');
  const bouncedEvent = allEventHistory.find(e => e.eventType === 'failed');
  const complainedEvent = allEventHistory.find(e => e.eventType === 'complained');
  const unsubscribedEvent = allEventHistory.find(e => e.eventType === 'unsubscribed');

  // Get the most recent event for the main timestamp
  const latestEvent = allEventHistory[allEventHistory.length - 1];
  const firstEvent = sortedEvents[0];

  // Build the aggregated data
  const aggregatedData: AggregatedMessageData = {
    // Basic tracking data
    id: existingData?.id || messageId,
    recipient: firstEvent.recipient,
    subject: existingData?.subject || 'Unknown Subject', // This would typically come from message details
    status: finalStatus,
    timestamp: latestEvent.timestamp,
    
    // Enhanced tracking data
    mailgunMessageId: messageId,
    deliveredAt: deliveredEvent?.timestamp,
    openedAt: openedEvent?.timestamp,
    clickedAt: clickedEvent?.timestamp,
    bouncedAt: bouncedEvent?.timestamp,
    complainedAt: complainedEvent?.timestamp,
    unsubscribedAt: unsubscribedEvent?.timestamp,
    
    // Error information
    error: bouncedEvent?.eventData?.deliveryStatus?.message,
    bounceReason: bouncedEvent?.eventData?.deliveryStatus?.message,
    
    // Client and location info from the most recent engagement event
    userAgent: getLatestEngagementData(allEventHistory)?.userAgent,
    clientInfo: getLatestEngagementData(allEventHistory)?.clientInfo,
    geolocation: getLatestEngagementData(allEventHistory)?.geolocation,
    
    // Aggregation-specific data
    eventHistory: allEventHistory,
    openCount,
    clickCount,
    lastUpdated: new Date().toISOString()
  };

  return aggregatedData;
};

/**
 * Merges two event histories, removing duplicates and maintaining chronological order
 * 
 * @param existingHistory - Existing event history
 * @param newHistory - New events to merge in
 * @returns Merged and deduplicated event history
 */
const mergeEventHistories = (
  existingHistory: EventHistoryEntry[],
  newHistory: EventHistoryEntry[]
): EventHistoryEntry[] => {
  // Combine all events
  const allEvents = [...existingHistory, ...newHistory];
  
  // Remove duplicates based on event type and timestamp
  const uniqueEvents = allEvents.filter((event, index, array) => {
    return array.findIndex(e => 
      e.eventType === event.eventType && 
      e.timestamp === event.timestamp
    ) === index;
  });
  
  // Sort chronologically
  return uniqueEvents.sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
};

/**
 * Gets the most recent engagement event data (opened or clicked) for client/location info
 * 
 * @param eventHistory - Complete event history
 * @returns Event data from the most recent engagement event
 */
const getLatestEngagementData = (eventHistory: EventHistoryEntry[]) => {
  const engagementEvents = eventHistory
    .filter(e => e.eventType === 'opened' || e.eventType === 'clicked')
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  return engagementEvents[0]?.eventData;
};

/**
 * Updates existing message tracking data with new events
 * 
 * @param existingData - Current message tracking data
 * @param newEvents - New Mailgun events to incorporate
 * @returns Updated aggregated message data
 * 
 * @example
 * ```typescript
 * const updated = updateMessageWithNewEvents(existingTrackingData, [newOpenEvent]);
 * // Returns updated data with new open event added to history
 * ```
 */
export const updateMessageWithNewEvents = (
  existingData: AggregatedMessageData,
  newEvents: MailgunEvent[]
): AggregatedMessageData => {
  if (newEvents.length === 0) {
    return existingData;
  }

  // Validate that new events are for the same message
  const messageId = existingData.mailgunMessageId;
  const invalidEvents = newEvents.filter(event => 
    event.message?.headers?.['message-id'] !== messageId
  );
  
  if (invalidEvents.length > 0) {
    throw new Error(`New events must be for the same message (${messageId}). Found ${invalidEvents.length} events with different message IDs`);
  }

  // Aggregate the new events with existing data
  return aggregateMessageEvents(newEvents, existingData);
};

/**
 * Groups multiple Mailgun events by message ID for batch processing
 * 
 * @param events - Array of Mailgun events from potentially different messages
 * @returns Map of message ID to array of events for that message
 * 
 * @example
 * ```typescript
 * const eventsByMessage = groupEventsByMessageId(allEvents);
 * // Returns: Map { 'msg-123' => [event1, event2], 'msg-456' => [event3] }
 * ```
 */
export const groupEventsByMessageId = (events: MailgunEvent[]): Map<string, MailgunEvent[]> => {
  const groupedEvents = new Map<string, MailgunEvent[]>();
  
  for (const event of events) {
    const messageId = event.message?.headers?.['message-id'];
    if (!messageId) {
      console.warn('Skipping event without valid message ID:', event.id);
      continue;
    }
    
    if (!groupedEvents.has(messageId)) {
      groupedEvents.set(messageId, []);
    }
    
    groupedEvents.get(messageId)!.push(event);
  }
  
  return groupedEvents;
};

/**
 * Processes multiple messages worth of events and returns aggregated tracking data
 * 
 * @param events - Array of Mailgun events from multiple messages
 * @param existingData - Optional map of existing tracking data by message ID
 * @returns Map of message ID to aggregated tracking data
 * 
 * @example
 * ```typescript
 * const trackingData = batchAggregateMessageEvents(allEvents, existingTrackingMap);
 * // Returns: Map { 'msg-123' => AggregatedMessageData, 'msg-456' => AggregatedMessageData }
 * ```
 */
export const batchAggregateMessageEvents = (
  events: MailgunEvent[],
  existingData?: Map<string, AggregatedMessageData>
): Map<string, AggregatedMessageData> => {
  const groupedEvents = groupEventsByMessageId(events);
  const results = new Map<string, AggregatedMessageData>();
  
  // Use forEach to iterate over the Map entries
  groupedEvents.forEach((messageEvents, messageId) => {
    try {
      const existingMessageData = existingData?.get(messageId);
      const aggregatedData = aggregateMessageEvents(messageEvents, existingMessageData);
      results.set(messageId, aggregatedData);
    } catch (error) {
      console.error(`Failed to aggregate events for message ${messageId}:`, error);
      // Continue processing other messages even if one fails
    }
  });
  
  return results;
};

// ============================================================================
// DERIVED METRICS CALCULATION
// ============================================================================

/**
 * Interface for time-based metrics calculation options
 */
export interface TimeBasedMetricsOptions {
  /** Time window in hours for calculating metrics (default: 24) */
  timeWindowHours?: number;
  /** Reference timestamp for calculating time-based metrics (default: now) */
  referenceTime?: string;
}

/**
 * Interface for enhanced campaign statistics with time-based metrics
 */
export interface EnhancedCampaignStats {
  /** Basic campaign statistics */
  campaignId: string;
  totalSent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  
  /** Calculated rate metrics (as percentages) */
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  complaintRate: number;
  unsubscribeRate: number;
  
  /** Time-based engagement metrics */
  opensPerHour: number;
  clicksPerHour: number;
  
  /** Advanced engagement metrics */
  clickToOpenRate: number; // (clicked/opened * 100)
  engagementRate: number;  // ((opened + clicked)/delivered * 100)
  
  /** Timestamp when metrics were calculated */
  calculatedAt: string;
}

/**
 * Calculates delivery rate as a percentage
 * 
 * @param delivered - Number of messages delivered
 * @param totalSent - Total number of messages sent
 * @returns Delivery rate as percentage (0-100), or 0 if totalSent is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateDeliveryRate(95, 100); // Returns 95.0
 * const zeroRate = calculateDeliveryRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateDeliveryRate = (delivered: number, totalSent: number): number => {
  if (totalSent === 0) return 0;
  return Math.round((delivered / totalSent) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates open rate as a percentage
 * 
 * @param opened - Number of messages opened
 * @param delivered - Number of messages delivered
 * @returns Open rate as percentage (0-100), or 0 if delivered is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateOpenRate(30, 95); // Returns 31.58
 * const zeroRate = calculateOpenRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateOpenRate = (opened: number, delivered: number): number => {
  if (delivered === 0) return 0;
  return Math.round((opened / delivered) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates click rate as a percentage (clicks per delivered message)
 * 
 * @param clicked - Number of messages with clicks
 * @param delivered - Number of messages delivered
 * @returns Click rate as percentage (0-100), or 0 if delivered is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateClickRate(15, 95); // Returns 15.79
 * const zeroRate = calculateClickRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateClickRate = (clicked: number, delivered: number): number => {
  if (delivered === 0) return 0;
  return Math.round((clicked / delivered) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates click-to-open rate as a percentage (clicks per opened message)
 * 
 * @param clicked - Number of messages with clicks
 * @param opened - Number of messages opened
 * @returns Click-to-open rate as percentage (0-100), or 0 if opened is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateClickToOpenRate(15, 30); // Returns 50.0
 * const zeroRate = calculateClickToOpenRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateClickToOpenRate = (clicked: number, opened: number): number => {
  if (opened === 0) return 0;
  return Math.round((clicked / opened) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates bounce rate as a percentage
 * 
 * @param bounced - Number of messages that bounced
 * @param totalSent - Total number of messages sent
 * @returns Bounce rate as percentage (0-100), or 0 if totalSent is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateBounceRate(5, 100); // Returns 5.0
 * const zeroRate = calculateBounceRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateBounceRate = (bounced: number, totalSent: number): number => {
  if (totalSent === 0) return 0;
  return Math.round((bounced / totalSent) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates complaint rate as a percentage
 * 
 * @param complained - Number of messages that received complaints
 * @param delivered - Number of messages delivered
 * @returns Complaint rate as percentage (0-100), or 0 if delivered is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateComplaintRate(2, 95); // Returns 2.11
 * const zeroRate = calculateComplaintRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateComplaintRate = (complained: number, delivered: number): number => {
  if (delivered === 0) return 0;
  return Math.round((complained / delivered) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates unsubscribe rate as a percentage
 * 
 * @param unsubscribed - Number of recipients who unsubscribed
 * @param delivered - Number of messages delivered
 * @returns Unsubscribe rate as percentage (0-100), or 0 if delivered is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateUnsubscribeRate(3, 95); // Returns 3.16
 * const zeroRate = calculateUnsubscribeRate(0, 0); // Returns 0.0
 * ```
 */
export const calculateUnsubscribeRate = (unsubscribed: number, delivered: number): number => {
  if (delivered === 0) return 0;
  return Math.round((unsubscribed / delivered) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates overall engagement rate as a percentage
 * Engagement is defined as messages that were either opened or clicked
 * 
 * @param opened - Number of messages opened
 * @param clicked - Number of messages with clicks
 * @param delivered - Number of messages delivered
 * @returns Engagement rate as percentage (0-100), or 0 if delivered is 0
 * 
 * @example
 * ```typescript
 * const rate = calculateEngagementRate(30, 15, 95); // Returns 47.37 (45 unique engagements)
 * const zeroRate = calculateEngagementRate(0, 0, 0); // Returns 0.0
 * ```
 */
export const calculateEngagementRate = (opened: number, clicked: number, delivered: number): number => {
  if (delivered === 0) return 0;
  // Note: We assume that clicked messages were also opened, so we don't double-count
  // The engagement is the unique number of messages that had any interaction
  const uniqueEngagements = Math.max(opened, clicked); // clicked should be <= opened
  return Math.round((uniqueEngagements / delivered) * 10000) / 100; // Round to 2 decimal places
};

/**
 * Calculates time-based metrics (events per hour) within a specified time window
 * 
 * @param events - Array of aggregated message data
 * @param options - Options for time-based calculation
 * @returns Object with opens per hour and clicks per hour
 * 
 * @example
 * ```typescript
 * const metrics = calculateTimeBasedMetrics(messages, { timeWindowHours: 24 });
 * // Returns: { opensPerHour: 1.25, clicksPerHour: 0.5 }
 * ```
 */
export const calculateTimeBasedMetrics = (
  events: AggregatedMessageData[],
  options: TimeBasedMetricsOptions = {}
): { opensPerHour: number; clicksPerHour: number } => {
  const { timeWindowHours = 24, referenceTime = new Date().toISOString() } = options;
  const referenceTimestamp = new Date(referenceTime).getTime();
  const timeWindowMs = timeWindowHours * 60 * 60 * 1000;
  const cutoffTime = referenceTimestamp - timeWindowMs;
  
  let totalOpens = 0;
  let totalClicks = 0;
  
  for (const message of events) {
    // Count opens within time window
    if (message.openedAt) {
      const openTime = new Date(message.openedAt).getTime();
      if (openTime >= cutoffTime) {
        totalOpens += message.openCount || 1; // Use openCount if available, otherwise 1
      }
    }
    
    // Count clicks within time window
    if (message.clickedAt) {
      const clickTime = new Date(message.clickedAt).getTime();
      if (clickTime >= cutoffTime) {
        totalClicks += message.clickCount || 1; // Use clickCount if available, otherwise 1
      }
    }
  }
  
  const opensPerHour = Math.round((totalOpens / timeWindowHours) * 100) / 100;
  const clicksPerHour = Math.round((totalClicks / timeWindowHours) * 100) / 100;
  
  return { opensPerHour, clicksPerHour };
};

/**
 * Generates comprehensive campaign statistics from aggregated message data
 * 
 * @param messages - Array of aggregated message tracking data
 * @param campaignId - Unique identifier for the campaign
 * @param options - Options for time-based metrics calculation
 * @returns Enhanced campaign statistics with all calculated metrics
 * 
 * @example
 * ```typescript
 * const stats = generateCampaignStats(aggregatedMessages, 'campaign-123');
 * console.log(`Delivery Rate: ${stats.deliveryRate}%`);
 * console.log(`Open Rate: ${stats.openRate}%`);
 * console.log(`Click Rate: ${stats.clickRate}%`);
 * ```
 */
export const generateCampaignStats = (
  messages: AggregatedMessageData[],
  campaignId: string,
  options: TimeBasedMetricsOptions = {}
): EnhancedCampaignStats => {
  // Count messages by status
  let totalSent = 0;
  let delivered = 0;
  let opened = 0;
  let clicked = 0;
  let bounced = 0;
  let complained = 0;
  let unsubscribed = 0;
  
  for (const message of messages) {
    totalSent++;
    
    // Count based on final status and event history
    if (message.status === 'delivered' || message.status === 'opened' || message.status === 'clicked') {
      delivered++;
    }
    
    if (message.status === 'opened' || message.status === 'clicked') {
      opened++;
    }
    
    if (message.status === 'clicked') {
      clicked++;
    }
    
    if (message.status === 'failed') {
      // Check if it's a bounce by looking at event history
      const hasBounce = message.eventHistory.some(event => 
        event.eventType === 'failed' && 
        event.eventData?.deliveryStatus?.code && 
        event.eventData.deliveryStatus.code >= 400
      );
      if (hasBounce) {
        bounced++;
      }
    }
    
    // Check for complaints and unsubscribes in event history
    const hasComplaint = message.eventHistory.some(event => event.eventType === 'complained');
    const hasUnsubscribe = message.eventHistory.some(event => event.eventType === 'unsubscribed');
    
    if (hasComplaint) complained++;
    if (hasUnsubscribe) unsubscribed++;
  }
  
  // Calculate all rate metrics
  const deliveryRate = calculateDeliveryRate(delivered, totalSent);
  const openRate = calculateOpenRate(opened, delivered);
  const clickRate = calculateClickRate(clicked, delivered);
  const clickToOpenRate = calculateClickToOpenRate(clicked, opened);
  const bounceRate = calculateBounceRate(bounced, totalSent);
  const complaintRate = calculateComplaintRate(complained, delivered);
  const unsubscribeRate = calculateUnsubscribeRate(unsubscribed, delivered);
  const engagementRate = calculateEngagementRate(opened, clicked, delivered);
  
  // Calculate time-based metrics
  const { opensPerHour, clicksPerHour } = calculateTimeBasedMetrics(messages, options);
  
  return {
    campaignId,
    totalSent,
    delivered,
    opened,
    clicked,
    bounced,
    complained,
    unsubscribed,
    deliveryRate,
    openRate,
    clickRate,
    clickToOpenRate,
    bounceRate,
    complaintRate,
    unsubscribeRate,
    engagementRate,
    opensPerHour,
    clicksPerHour,
    calculatedAt: new Date().toISOString(),
  };
};

/**
 * Converts basic MessageTrackingData to EnhancedMessageTrackingData format
 * This is useful for upgrading legacy tracking data to the new enhanced format
 * 
 * @param basicData - Basic message tracking data
 * @param mailgunMessageId - Mailgun's unique message identifier
 * @returns Enhanced message tracking data with additional fields
 * 
 * @example
 * ```typescript
 * const enhanced = convertToEnhancedTrackingData(basicMessage, 'mg-msg-123');
 * // Returns EnhancedMessageTrackingData with all required fields
 * ```
 */
export const convertToEnhancedTrackingData = (
  basicData: MessageTrackingData,
  mailgunMessageId: string
): EnhancedMessageTrackingData => {
  return {
    ...basicData,
    mailgunMessageId,
    deliveredAt: basicData.status === 'delivered' || basicData.status === 'opened' || basicData.status === 'clicked' 
      ? basicData.timestamp 
      : undefined,
  };
};

/**
 * Batch converts multiple basic tracking data records to enhanced format
 * 
 * @param basicDataArray - Array of basic message tracking data
 * @param getMailgunMessageId - Function to extract Mailgun message ID from basic data
 * @returns Array of enhanced message tracking data
 * 
 * @example
 * ```typescript
 * const enhanced = batchConvertToEnhancedTrackingData(
 *   basicMessages,
 *   (msg) => `mg-${msg.id}`
 * );
 * ```
 */
export const batchConvertToEnhancedTrackingData = (
  basicDataArray: MessageTrackingData[],
  getMailgunMessageId: (data: MessageTrackingData) => string
): EnhancedMessageTrackingData[] => {
  return basicDataArray.map(basicData => 
    convertToEnhancedTrackingData(basicData, getMailgunMessageId(basicData))
  );
};

// ============================================================================
// EVENT PAYLOAD EXTRACTION UTILITIES
// ============================================================================

/**
 * Extracted recipient information from Mailgun event payload
 */
export interface ExtractedRecipientInfo {
  /** Primary recipient email address */
  email: string;
  /** Recipient domain (extracted from email) */
  domain: string;
  /** Recipient name if available */
  name?: string;
}

/**
 * Extracted message identifiers from Mailgun event payload
 */
export interface ExtractedMessageIds {
  /** Mailgun's unique message identifier */
  mailgunMessageId: string;
  /** Original message ID from headers */
  originalMessageId?: string;
  /** Thread ID if part of a conversation */
  threadId?: string;
}

/**
 * Event-specific data extracted from Mailgun payload
 */
export interface ExtractedEventData {
  /** Event type (normalized) */
  eventType: MailgunEventType;
  /** Event timestamp (standardized to ISO) */
  timestamp: string;
  /** Delivery status information for failed events */
  deliveryStatus?: {
    code?: number;
    message?: string;
    description?: string;
  };
  /** URL clicked (for click events) */
  clickedUrl?: string;
  /** IP address of the recipient */
  ipAddress?: string;
  /** User agent string from recipient's client */
  userAgent?: string;
}

/**
 * Extracted metadata and tags from Mailgun event payload
 */
export interface ExtractedMetadata {
  /** Campaign tags associated with the message */
  tags: string[];
  /** Campaign identifiers */
  campaigns: string[];
  /** Custom variables attached to the message */
  customVariables: Record<string, string>;
  /** Storage information for stored messages */
  storage?: {
    url: string;
    key: string;
  };
}

/**
 * Client information extracted from Mailgun event payload
 */
export interface ExtractedClientInfo {
  /** Client application name */
  name?: string;
  /** Operating system */
  os?: string;
  /** Device type */
  device?: string;
  /** Full user agent string */
  userAgent?: string;
}

/**
 * Geographic location information extracted from Mailgun event payload
 */
export interface ExtractedGeolocation {
  /** Country code or name */
  country?: string;
  /** Region or state */
  region?: string;
  /** City name */
  city?: string;
}

/**
 * Complete extracted payload data from a Mailgun event
 */
export interface ExtractedPayloadData {
  /** Recipient information */
  recipient: ExtractedRecipientInfo;
  /** Message identifiers */
  messageIds: ExtractedMessageIds;
  /** Event-specific data */
  eventData: ExtractedEventData;
  /** Metadata and tags */
  metadata: ExtractedMetadata;
  /** Client information */
  clientInfo?: ExtractedClientInfo;
  /** Geographic location */
  geolocation?: ExtractedGeolocation;
}

/**
 * Extracts recipient information from a Mailgun event payload
 * 
 * @param event - Mailgun event object
 * @returns Extracted recipient information
 * 
 * @example
 * ```typescript
 * const recipientInfo = extractRecipientInfo(mailgunEvent);
 * // Returns { email: "user@example.com", domain: "example.com" }
 * ```
 */
export const extractRecipientInfo = (event: MailgunEvent): ExtractedRecipientInfo => {
  if (!event.recipient) {
    throw new Error('Missing recipient information in Mailgun event');
  }

  const email = event.recipient.toLowerCase().trim();
  
  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error(`Invalid email format: ${email}`);
  }

  // Extract domain from email
  const domain = event['recipient-domain'] || email.split('@')[1];

  return {
    email,
    domain: domain.toLowerCase(),
    // Name might be available in custom variables or other fields
    name: undefined
  };
};

/**
 * Extracts message identifiers from a Mailgun event payload
 * 
 * @param event - Mailgun event object
 * @returns Extracted message identifiers
 * 
 * @example
 * ```typescript
 * const messageIds = extractMessageIds(mailgunEvent);
 * // Returns { mailgunMessageId: "msg-123", originalMessageId: "original-456" }
 * ```
 */
export const extractMessageIds = (event: MailgunEvent): ExtractedMessageIds => {
  // Extract Mailgun's message ID from headers
  const mailgunMessageId = event.message?.headers?.['message-id'];
  
  if (!mailgunMessageId) {
    throw new Error('Missing message ID in Mailgun event headers');
  }

  return {
    mailgunMessageId,
    originalMessageId: event.message?.headers?.['message-id'],
    // Thread ID might be available in custom headers
    threadId: undefined
  };
};

/**
 * Extracts event-specific data from a Mailgun event payload
 * 
 * @param event - Mailgun event object
 * @returns Extracted event data
 * 
 * @example
 * ```typescript
 * const eventData = extractEventData(mailgunEvent);
 * // Returns { eventType: "delivered", timestamp: "2023-01-01T00:00:00.000Z" }
 * ```
 */
export const extractEventData = (event: MailgunEvent): ExtractedEventData => {
  if (!event.event) {
    throw new Error('Missing event type in Mailgun event');
  }

  if (!event.timestamp) {
    throw new Error('Missing timestamp in Mailgun event');
  }

  // Normalize event type
  const eventType = event.event.toLowerCase() as MailgunEventType;
  
  // Validate event type
  if (!isValidMailgunEventType(eventType)) {
    throw new Error(`Invalid Mailgun event type: ${event.event}`);
  }

  // Standardize timestamp
  const timestamp = standardizeTimestamp(event.timestamp);

  const result: ExtractedEventData = {
    eventType,
    timestamp,
    ipAddress: event.ip,
    userAgent: event['client-info']?.['user-agent']
  };

  // Extract delivery status for failed events
  if (event['delivery-status']) {
    result.deliveryStatus = {
      code: event['delivery-status'].code,
      message: event['delivery-status'].message,
      description: event['delivery-status'].message // Alias for consistency
    };
  }

  // Extract clicked URL for click events
  if (event.event === 'clicked' && event.url) {
    result.clickedUrl = event.url;
  }

  return result;
};

/**
 * Extracts metadata and tags from a Mailgun event payload
 * 
 * @param event - Mailgun event object
 * @returns Extracted metadata
 * 
 * @example
 * ```typescript
 * const metadata = extractMetadata(mailgunEvent);
 * // Returns { tags: ["campaign-1"], campaigns: ["newsletter"], customVariables: {} }
 * ```
 */
export const extractMetadata = (event: MailgunEvent): ExtractedMetadata => {
  const result: ExtractedMetadata = {
    tags: event.tags || [],
    campaigns: event.campaigns || [],
    customVariables: {}
  };

  // Extract storage information if available
  if (event.storage) {
    result.storage = {
      url: event.storage.url,
      key: event.storage.key
    };
  }

  // Extract custom variables from user-variables field
  // Mailgun prefixes custom variables with 'my-var-' in webhooks
  Object.keys(event).forEach(key => {
    if (key.startsWith('my-var-')) {
      const variableName = key.replace('my-var-', '');
      result.customVariables[variableName] = String(event[key as keyof MailgunEvent]);
    }
  });

  return result;
};

/**
 * Extracts client information from a Mailgun event payload
 * 
 * @param event - Mailgun event object
 * @returns Extracted client information or undefined if not available
 * 
 * @example
 * ```typescript
 * const clientInfo = extractClientInfo(mailgunEvent);
 * // Returns { name: "Gmail", os: "Windows", device: "Desktop" }
 * ```
 */
export const extractClientInfo = (event: MailgunEvent): ExtractedClientInfo | undefined => {
  if (!event['client-info']) {
    return undefined;
  }

  const clientInfo = event['client-info'];
  
  return {
    name: clientInfo['client-name'],
    os: clientInfo['client-os'],
    device: clientInfo['device-type'],
    userAgent: clientInfo['user-agent']
  };
};

/**
 * Extracts geographic location information from a Mailgun event payload
 * 
 * @param event - Mailgun event object
 * @returns Extracted geolocation or undefined if not available
 * 
 * @example
 * ```typescript
 * const location = extractGeolocation(mailgunEvent);
 * // Returns { country: "US", region: "CA", city: "San Francisco" }
 * ```
 */
export const extractGeolocation = (event: MailgunEvent): ExtractedGeolocation | undefined => {
  if (!event.geolocation) {
    return undefined;
  }

  return {
    country: event.geolocation.country,
    region: event.geolocation.region,
    city: event.geolocation.city
  };
};

/**
 * Extracts all relevant information from a Mailgun event payload
 * This is the main extraction function that combines all individual extractors
 * 
 * @param event - Mailgun event object
 * @returns Complete extracted payload data
 * 
 * @example
 * ```typescript
 * const extractedData = extractCompletePayloadData(mailgunEvent);
 * // Returns ExtractedPayloadData with all available information
 * ```
 */
export const extractCompletePayloadData = (event: MailgunEvent): ExtractedPayloadData => {
  try {
    const recipient = extractRecipientInfo(event);
    const messageIds = extractMessageIds(event);
    const eventData = extractEventData(event);
    const metadata = extractMetadata(event);
    const clientInfo = extractClientInfo(event);
    const geolocation = extractGeolocation(event);

    return {
      recipient,
      messageIds,
      eventData,
      metadata,
      clientInfo,
      geolocation
    };
  } catch (error) {
    throw new Error(`Failed to extract payload data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Maps a Mailgun event to MessageTrackingData format
 * This is the core transformation function that converts raw Mailgun events
 * into the application's tracking data format
 * 
 * @param event - Mailgun event object
 * @param subject - Email subject (may not be available in event payload)
 * @returns MessageTrackingData object
 * 
 * @example
 * ```typescript
 * const trackingData = mapMailgunEventToTrackingData(mailgunEvent, "Welcome Email");
 * // Returns MessageTrackingData with standardized format
 * ```
 */
export const mapMailgunEventToTrackingData = (
  event: MailgunEvent, 
  subject?: string
): MessageTrackingData => {
  try {
    // Extract all payload data
    const extracted = extractCompletePayloadData(event);
    
    // Map event type to application status
    const status = mapEventTypeToStatus(extracted.eventData.eventType);
    
    // Create base tracking data
    const trackingData: MessageTrackingData = {
      id: extracted.messageIds.mailgunMessageId,
      recipient: extracted.recipient.email,
      subject: subject || 'Unknown Subject',
      status,
      timestamp: extracted.eventData.timestamp
    };

    // Add event-specific timestamps
    switch (extracted.eventData.eventType) {
      case 'opened':
        trackingData.openedAt = extracted.eventData.timestamp;
        break;
      case 'clicked':
        trackingData.clickedAt = extracted.eventData.timestamp;
        break;
      case 'failed':
      case 'rejected':
        trackingData.error = extracted.eventData.deliveryStatus?.message || 'Delivery failed';
        break;
    }

    return trackingData;
  } catch (error) {
    throw new Error(`Failed to map Mailgun event to tracking data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Maps a Mailgun event to EnhancedMessageTrackingData format
 * This function provides the full enhanced tracking data with all available information
 * 
 * @param event - Mailgun event object
 * @param subject - Email subject (may not be available in event payload)
 * @returns EnhancedMessageTrackingData object
 * 
 * @example
 * ```typescript
 * const enhancedData = mapMailgunEventToEnhancedTrackingData(mailgunEvent, "Welcome Email");
 * // Returns EnhancedMessageTrackingData with comprehensive information
 * ```
 */
export const mapMailgunEventToEnhancedTrackingData = (
  event: MailgunEvent,
  subject?: string
): EnhancedMessageTrackingData => {
  try {
    // Get basic tracking data first
    const basicData = mapMailgunEventToTrackingData(event, subject);
    
    // Extract complete payload data
    const extracted = extractCompletePayloadData(event);
    
    // Create enhanced tracking data
    const enhancedData: EnhancedMessageTrackingData = {
      ...basicData,
      mailgunMessageId: extracted.messageIds.mailgunMessageId,
      userAgent: extracted.eventData.userAgent,
    };

    // Add event-specific enhanced data
    switch (extracted.eventData.eventType) {
      case 'delivered':
        enhancedData.deliveredAt = extracted.eventData.timestamp;
        break;
      case 'failed':
      case 'rejected':
        enhancedData.bouncedAt = extracted.eventData.timestamp;
        enhancedData.bounceReason = extracted.eventData.deliveryStatus?.message;
        break;
      case 'complained':
        enhancedData.complainedAt = extracted.eventData.timestamp;
        break;
      case 'unsubscribed':
        enhancedData.unsubscribedAt = extracted.eventData.timestamp;
        break;
    }

    // Add client information if available
    if (extracted.clientInfo) {
      enhancedData.clientInfo = {
        name: extracted.clientInfo.name || 'Unknown',
        os: extracted.clientInfo.os || 'Unknown',
        device: extracted.clientInfo.device || 'Unknown'
      };
    }

    // Add geolocation if available
    if (extracted.geolocation) {
      enhancedData.geolocation = {
        country: extracted.geolocation.country || 'Unknown',
        region: extracted.geolocation.region || 'Unknown',
        city: extracted.geolocation.city || 'Unknown'
      };
    }

    return enhancedData;
  } catch (error) {
    throw new Error(`Failed to map Mailgun event to enhanced tracking data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Batch processes multiple Mailgun events into tracking data
 * 
 * @param events - Array of Mailgun events
 * @param getSubject - Function to extract subject for each event
 * @returns Array of MessageTrackingData objects
 * 
 * @example
 * ```typescript
 * const trackingDataArray = batchMapMailgunEventsToTrackingData(
 *   events,
 *   (event) => `Campaign ${event.campaigns?.[0] || 'Unknown'}`
 * );
 * ```
 */
export const batchMapMailgunEventsToTrackingData = (
  events: MailgunEvent[],
  getSubject?: (event: MailgunEvent) => string
): MessageTrackingData[] => {
  return events.map(event => {
    const subject = getSubject ? getSubject(event) : undefined;
    return mapMailgunEventToTrackingData(event, subject);
  });
};

/**
 * Batch processes multiple Mailgun events into enhanced tracking data
 * 
 * @param events - Array of Mailgun events
 * @param getSubject - Function to extract subject for each event
 * @returns Array of EnhancedMessageTrackingData objects
 * 
 * @example
 * ```typescript
 * const enhancedDataArray = batchMapMailgunEventsToEnhancedTrackingData(
 *   events,
 *   (event) => `Campaign ${event.campaigns?.[0] || 'Unknown'}`
 * );
 * ```
 */
export const batchMapMailgunEventsToEnhancedTrackingData = (
  events: MailgunEvent[],
  getSubject?: (event: MailgunEvent) => string
): EnhancedMessageTrackingData[] => {
  return events.map(event => {
    const subject = getSubject ? getSubject(event) : undefined;
    return mapMailgunEventToEnhancedTrackingData(event, subject);
  });
};